import { lazy, Suspense } from 'react';
import {
  Navigate,
  Outlet,
  RouteObject,
  createBrowserRouter,
} from 'react-router-dom';
import App from 'App';
import { OnlyPublicAuth, RequireAuth } from 'utils/routeComponents';
import MainLayoutProvider from 'providers/MainLayoutProvider';
import MainLayout from 'layouts/MainLayout';
import {
  LOGIN,
  DASHBOARD,
  PROPERTY_LISTING,
  ROLE_PERMISSIONS,
  ENQUIRY,
  PROPERTY_ENQUIRY,
  USER_REQUIREMENTS,
  SOLDOUT_INQUIRY,
  POTENTIAL_BUYER,
  DEVELOPER,
  LOCALITY,
  DEVELOPER_FORM,
  LOCALITY_FORM,
  PROPERTY_REPORT,
  PROJECT,
  PROJECT_FORM,
  PROPERTY_FORM,
  UPDATE_PROJECT_FORM,
  PROJECT_PROMOTION,
  PROJECT_PROMOTION_FORM,
  PROJECT_PROMOTION_UPDATE_FORM,
  PROJECT_ENQUIRY,
  BLOGS,
  BLOGS_FORM,
  SEO_CONTENT,
  SEO_CONTENT_FORM,
  PROPERTY_FORM_ADD,
  ADD_PROPERTY_LISTING,
  UPDATE_PROPERTY_FORM,
  ADMIN,
  ADD_MASTER_EMPLOYEE,
  PROJECT_COINVESTMENT,
  ADD_PROJECT_COINVESTMENT,
  UPDATE_PROJECT_COINVESTMENT,
  CHATINFO,
  MESSAGE_SERVICE,
  CAMPAIGNS,
  CREATE_CAMPAIGN,
  EDIT_CAMPAIGN,
  CAMPAIGN_DETAILS,
  CAMPAIGN_TRANSACTION_DETAILS,
  CRONS,
  CREATE_CRON,
  CREATE_REGULAR_CRON,
  CREATE_PERIODIC_CRON,
  EDIT_CRON,
  CRON_DETAILS,
  CRON_TRANSACTION_DETAILS,
  TEMPLATES,
  CREATE_TEMPLATE,
  EDIT_TEMPLATE,
  TEMPLATE_DETAILS,
  WHATSAPP_SEND,
  SUBSCRIPTION,
  SUBSCRIPTION_ANALYTICS,
  SUBSCRIPTION_FORM,
  BUILDER_SUBSCRIPTION,
  BUILDER_SUBSCRIPTION_EDIT,
  BUILDER_SUBSCRIPTION_FORM,
  ROLES,
  PROFILE,
  USER_SUBSCRIPTION,
  LOCALITY_DESCRIPTIONS,
  LOCALITY_CONTENT_FORM,
} from 'constants/RouteUrls';
import UserSubscription from 'components/modules/subscription/UserSubscription';

// Lazy load components
const CardSignIn = lazy(() => import('pages/pages/authentication/card/SignIn'));
const UsersTable = lazy(() => import('components/modules/users/UsersTable').then(module => ({ default: module.UsersTable })));
const AdminDashboard = lazy(() => import('components/modules/admin/AdminDashboard'));
const PropertyListing = lazy(() => import('components/modules/property-listing/PropertyListing').then(module => ({ default: module.PropertyListing })));
const RoleAndPermissions = lazy(() => import('components/modules/role-permissions/RoleAndPermissions').then(module => ({ default: module.RoleAndPermissions })));
const Enquiry = lazy(() => import('components/modules/enquiry/Enquiry').then(module => ({ default: module.Enquiry })));
const PropertyEnquiry = lazy(() => import('components/modules/property-enquiry/PropertyEnquiry').then(module => ({ default: module.PropertyEnquiry })));
const SoldOutInquiry = lazy(() => import('components/modules/enquiry/SoldOutInquiry').then(module => ({ default: module.SoldOutInquiry })));
const PotentialBuyer = lazy(() => import('components/modules/potential-buyer/PotentialBuyer').then(module => ({ default: module.PotentialBuyer })));
const Locality = lazy(() => import('components/modules/locality/Locality'));
const Developer = lazy(() => import('components/modules/developer/Developer'));
const DeveloperForm = lazy(() => import('components/modules/developer/DeveloperForm'));
const LocalityForm = lazy(() => import('components/modules/locality/LocalityForm'));
const PropertyReport = lazy(() => import('components/modules/property-report/PropertyReport').then(module => ({ default: module.PropertyReport })));
const ProjectListing = lazy(() => import('components/modules/Project-listing/ProjectListing').then(module => ({ default: module.ProjectListing })));
const ProjectForm = lazy(() => import('components/modules/Project-listing/ProjectForm').then(module => ({ default: module.ProjectForm })));
const PropertyForm = lazy(() => import('components/modules/property-listing/PropertyForm').then(module => ({ default: module.PropertyForm })));
const ProjectPromotionListing = lazy(() => import('components/modules/project-promotion/ProjectPromotionListing').then(module => ({ default: module.ProjectPromotionListing })));
const ProjectPromotionForm = lazy(() => import('components/modules/project-promotion/ProjectPromotionForm').then(module => ({ default: module.ProjectPromotionForm })));
const ProjectLinkVideoListing = lazy(() => import('components/modules/project-link-video/ProjectLinkVideoListing').then(module => ({ default: module.ProjectLinkVideoListing })));
const ProjectLinkVideoAdd = lazy(() => import('components/modules/project-link-video/ProjectLinkVideoAdd').then(module => ({ default: module.ProjectLinkVideoAdd })));
const ProjectEnquiry = lazy(() => import('components/modules/project-enquiry/ProjectEnquiry'));
const UserRequirements = lazy(() => import('components/modules/user-requirements/UserRequirements').then(module => ({ default: module.UserRequirements })));
const Blogs = lazy(() => import('components/modules/blogs/Blogs'));
const BlogForm = lazy(() => import('components/modules/blogs/BlogForm'));
const SeoContent = lazy(() => import('components/modules/seo-content/SeoContent'));
const LocalitiesDescriptions = lazy(() => import('components/modules/locality-descriptions/localityDescriptions'));
const SeoContentForm = lazy(() => import('components/modules/seo-content/SeoContentForm'));
const LocalityContentForm = lazy(() => import('components/modules/locality-descriptions/localityContentForm'));
const PropertyAddForm = lazy(() => import('components/modules/property-listing/PropertyAddForm').then(module => ({ default: module.PropertyAddForm })));
const PropertyEditForm = lazy(() => import('components/modules/property-listing/PropertyEditForm').then(module => ({ default: module.PropertyEditForm })));
const AddPropertyListing = lazy(() => import('components/modules/property-listing/AddPropertyListing/AddPropertyListing').then(module => ({ default: module.AddPropertyListing })));
const MainAdminLeads = lazy(() => import('components/modules/admin/MainAdminLeads'));
const AddMasterEmployee = lazy(() => import('components/modules/admin/AddMasterEmployee'));
const ProjectCoInvestment = lazy(() => import('components/project-co-investent/ProjectCoInvestment').then(module => ({ default: module.ProjectCoInvestment })));
const AddCoInvestmentProject = lazy(() => import('components/project-co-investent/AddCoInvestmentProject'));
const EditCoInvestmentProject = lazy(() => import('components/project-co-investent/EditCoInvestmentProject'));
const Chatinfo = lazy(() => import('components/modules/chatinfo/Chatinfo'));
const MessageService = lazy(() => import('components/modules/message-service/MessageService'));
const CreateCampaign = lazy(() => import('components/modules/message-service/CreateCampaign'));
const CampaignDetails = lazy(() => import('components/modules/message-service/CampaignDetails'));
const CampaignTransactionDetails = lazy(() => import('components/modules/message-service/CampaignTransactionDetails'));
const CronListing = lazy(() => import('components/modules/message-service/CronListing'));
const CronDetails = lazy(() => import('components/modules/message-service/CronDetails'));
const CronTransactionDetails = lazy(() => import('components/modules/message-service/CronTransactionDetails'));
const CreateCron = lazy(() => import('components/modules/message-service/CreateCron'));
const CreatePeriodiicCron = lazy(() => import('components/modules/message-service/CreatePeriodiicCron'));
const CronTypeSelector = lazy(() => import('components/modules/message-service/CronTypeSelector'));
const EditCron = lazy(() => import('components/modules/message-service/EditCron'));
const TemplateListing = lazy(() => import('components/modules/message-service/TemplateListing'));
const TemplateDetails = lazy(() => import('components/modules/message-service/TemplateDetails'));
const CreateTemplate = lazy(() => import('components/modules/message-service/CreateTemplate'));
const EditTemplate = lazy(() => import('components/modules/message-service/EditTemplate'));
const WhatsAppSend = lazy(() => import('components/modules/message-service/WhatsAppSend'));
const Subscription = lazy(() => import('components/modules/subscription/Subscription'));
const SubscriptionAnalytics = lazy(() => import('components/modules/subscription/SubscriptionAnalytics'));
const SubscriptionForm = lazy(() => import('components/modules/subscription/SubscriptionForm'));
const BuilderSubscription = lazy(() => import('components/modules/subscription/BuilderSubscription'));
const BuilderSubscriptionForm = lazy(() => import('components/modules/subscription/BuilderSubscriptionForm'));
const BuilderSubscriptionEdit = lazy(() => import('components/modules/subscription/BuilderSubscriptionEdit'));
const Roles = lazy(() => import('components/modules/roles/Roles'));
const Profile = lazy(() => import('pages/modules/profile/Profile'));

const SuspenseLayout = () => (
  <Suspense fallback={<div>Loading...</div>}>
    <Outlet />
  </Suspense>
);

const routes: RouteObject[] = [
  {
    element: <App />,
    children: [
      {
        path: LOGIN,
        element: (
          <OnlyPublicAuth redirectTo={'/dashboard'}>
            <Suspense fallback={<div>Loading...</div>}>
              <Outlet />
            </Suspense>
          </OnlyPublicAuth>
        ),
        children: [
          {
            path: LOGIN,
            element: <CardSignIn />,
          },
        ],
      },
      {
        path: '/',
        element: (
          <RequireAuth redirectTo={LOGIN}>
            <MainLayoutProvider>
              <MainLayout />
            </MainLayoutProvider>
          </RequireAuth>
        ),
        children: [
          {
            element: <SuspenseLayout />,
            children: [
              {
                path: '/',
                element: <AdminDashboard />,
              },
              {
                path: ADMIN,
                element: <MainAdminLeads />,
              },
              {
                path: ADD_MASTER_EMPLOYEE,
                element: <AddMasterEmployee />,
              },
              {
                path: DASHBOARD,
                element: <UsersTable />,
              },
              {
                path: PROPERTY_LISTING,
                element: <PropertyListing />,
              },
              {
                path: PROJECT,
                element: <ProjectListing />,
              },
              {
                path: PROJECT_FORM,
                element: <ProjectForm />,
              },
              {
                path: UPDATE_PROJECT_FORM,
                element: <ProjectForm />,
              },

              {
                path: PROJECT_PROMOTION,
                element: <ProjectPromotionListing />,
              },
              {
                path: PROJECT + '/link-video',
                element: <ProjectLinkVideoListing />,
              },
              {
                path: PROJECT + '/link-video/add',
                element: <ProjectLinkVideoAdd />,
              },
              {
                path: PROJECT_PROMOTION_FORM,
                element: <ProjectPromotionForm />,
              },
              {
                path: PROJECT_PROMOTION_UPDATE_FORM,
                element: <ProjectPromotionForm />,
              },
              {
                path: PROJECT_COINVESTMENT,
                element: <ProjectCoInvestment />,
              },
              {
                path: ADD_PROJECT_COINVESTMENT,
                element: <AddCoInvestmentProject />,
              },
              {
                path: UPDATE_PROJECT_COINVESTMENT,
                element: <EditCoInvestmentProject />,
              },
              {
                path: PROJECT_ENQUIRY,
                element: <ProjectEnquiry />,
              },
              {
                path: PROPERTY_FORM,
                element: <PropertyForm />,
              },
              {
                path: PROPERTY_FORM_ADD,
                element: <PropertyAddForm />,
              },
              {
                path: UPDATE_PROPERTY_FORM,
                element: <PropertyEditForm />,
              },
              {
                path: ADD_PROPERTY_LISTING,
                element: <AddPropertyListing />,
              },
              {
                path: ROLE_PERMISSIONS,
                element: <RoleAndPermissions />,
              },
              {
                path: ENQUIRY,
                element: <Enquiry />,
              },
              {
                path: PROPERTY_ENQUIRY,
                element: <PropertyEnquiry />,
              },
              {
                path: USER_REQUIREMENTS,
                element: <UserRequirements />,
              },
              {
                path: SOLDOUT_INQUIRY,
                element: <SoldOutInquiry />,
              },
              {
                path: POTENTIAL_BUYER,
                element: <PotentialBuyer />,
              },
              {
                path: DEVELOPER,
                element: <Developer />,
              },
              {
                path: DEVELOPER_FORM,
                element: <DeveloperForm />,
              },
              {
                path: LOCALITY,
                element: <Locality />,
              },
              {
                path: BLOGS,
                element: <Blogs />,
              },
              {
                path: SEO_CONTENT,
                element: <SeoContent />,
              },
             {
                path: LOCALITY_DESCRIPTIONS,
                element: <LocalitiesDescriptions />,
              },
              {
                path: SEO_CONTENT_FORM,
                element: <SeoContentForm />,
              },
              {
                path: LOCALITY_CONTENT_FORM,
                element: <LocalityContentForm />,
              },
              {
                path: SUBSCRIPTION,
                element: <Subscription />,
              },
              {
                path: SUBSCRIPTION_ANALYTICS,
                element: <SubscriptionAnalytics />,
              },
              {
                path: BUILDER_SUBSCRIPTION,
                element: <BuilderSubscription />,
              },
              {
                path: BUILDER_SUBSCRIPTION_EDIT,
                element: <BuilderSubscriptionEdit />,
              },
                {
                path: SUBSCRIPTION,
                element: <Subscription />,
              },
              {
                path: USER_SUBSCRIPTION,
                element: <UserSubscription />,
              },
              {
                path: ROLES,
                element: <Roles />,
              },
              {
                path: CHATINFO,
                element: <Chatinfo />,
              },
              {
                path: MESSAGE_SERVICE,
                element: <MessageService />,
              },
              {
                path: CAMPAIGNS,
                element: <MessageService />,
              },
              {
                path: CREATE_CAMPAIGN,
                element: <CreateCampaign />,
              },
              {
                path: EDIT_CAMPAIGN,
                element: <CreateCampaign />,
              },
              {
                path: CAMPAIGN_TRANSACTION_DETAILS,
                element: <CampaignTransactionDetails />,
              },
              {
                path: CAMPAIGN_DETAILS,
                element: <CampaignDetails />,
              },
              {
                path: CRONS,
                element: <CronListing />,
              },
              {
                path: CREATE_CRON,
                element: <CronTypeSelector />,
              },
              {
                path: CREATE_REGULAR_CRON,
                element: <CreateCron />,
              },
              {
                path: CREATE_PERIODIC_CRON,
                element: <CreatePeriodiicCron />,
              },
              {
                path: EDIT_CRON,
                element: <EditCron />,
              },
              {
                path: CRON_TRANSACTION_DETAILS,
                element: <CronTransactionDetails />,
              },
              {
                path: CRON_DETAILS,
                element: <CronDetails />,
              },
              {
                path: TEMPLATES,
                element: <TemplateListing />,
              },
              {
                path: CREATE_TEMPLATE,
                element: <CreateTemplate />,
              },
              {
                path: EDIT_TEMPLATE,
                element: <EditTemplate />,
              },
              {
                path: TEMPLATE_DETAILS,
                element: <TemplateDetails />,
              },
              {
                path: WHATSAPP_SEND,
                element: <WhatsAppSend />,
              },
              {
                path: LOCALITY_FORM,
                element: <LocalityForm />,
              },
              {
                path: BLOGS_FORM,
                element: <BlogForm />,
              },
              {
                path: SUBSCRIPTION_FORM,
                element: <SubscriptionForm />,
              },
              {
                path: BUILDER_SUBSCRIPTION_FORM,
                element: <BuilderSubscriptionForm />,
              },
              {
                path: PROPERTY_REPORT,
                element: <PropertyReport />,
              },
              {
                path: PROFILE,
                element: <Profile />,
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to={DASHBOARD} />,
      },
    ],
  },
];

export const router = createBrowserRouter(routes, {
  basename: '/',
});

export default routes;
